import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Stethoscope, Loader2, ShieldCheck, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

export default function CompleteProfile() {
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [hpcsa, setHpcsa] = useState("");
  const [roleType, setRoleType] = useState(user?.role_type === "student" ? "student" : "doctor");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const v = hpcsa.trim().toUpperCase();
    if (v.length < 3) {
      setErr("Please enter your full HPCSA / MP / student registration number.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/complete-hpcsa", { hpcsa_number: v, role_type: roleType });
      await refresh();
      toast.success("Profile complete — welcome to RapidCare SA");
      nav("/app/dashboard", { replace: true });
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not save your HPCSA number. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="complete-profile-page">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-[#111827]" />
            <span className="font-heading font-bold text-[#111827]">RapidCare SA</span>
          </div>
          <button
            type="button"
            onClick={() => { logout(); nav("/login", { replace: true }); }}
            className="text-xs text-slate-500 hover:text-[#111827]"
            data-testid="complete-profile-logout"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 py-12">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="w-12 h-12 rounded-xl bg-[#F1F5F9] text-[#111827] flex items-center justify-center mb-3">
              <BadgeCheck className="w-6 h-6" />
            </div>
            <CardTitle className="font-heading text-2xl">One last step{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}</CardTitle>
            <CardDescription>
              RapidCare SA is for HPCSA-verified clinicians and medical students only.
              Please add your registration number to finish setting up your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5" data-testid="complete-profile-form">
              <div className="space-y-2">
                <Label className="text-sm font-medium">I am a…</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setRoleType("doctor")}
                    className={`rounded-lg border px-4 py-3 text-sm font-medium text-left transition ${roleType === "doctor" ? "border-[#0B5FFF] bg-[#F1F5F9] text-[#111827]" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                    data-testid="role-doctor"
                  >
                    Qualified clinician
                    <div className="text-xs font-normal text-slate-500 mt-0.5">HPCSA / MP number</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoleType("student")}
                    className={`rounded-lg border px-4 py-3 text-sm font-medium text-left transition ${roleType === "student" ? "border-[#0B5FFF] bg-[#F1F5F9] text-[#111827]" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                    data-testid="role-student"
                  >
                    Medical student
                    <div className="text-xs font-normal text-slate-500 mt-0.5">University student no.</div>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hpcsa-input" className="text-sm font-medium">
                  {roleType === "student" ? "Student registration number" : "HPCSA / MP number"}
                </Label>
                <Input
                  id="hpcsa-input"
                  value={hpcsa}
                  onChange={(e) => setHpcsa(e.target.value)}
                  placeholder={roleType === "student" ? "e.g. STU-2024-12345" : "e.g. MP0123456"}
                  autoComplete="off"
                  required
                  data-testid="complete-profile-hpcsa-input"
                />
                <p className="text-xs text-slate-500">
                  We use this to verify clinical-professional access. It is not shared publicly.
                </p>
              </div>

              {err && (
                <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2.5" data-testid="complete-profile-error">
                  {err}
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-[#0B5FFF] hover:bg-[#0A54E0]"
                data-testid="complete-profile-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Finish setting up account
              </Button>

              <div className="flex items-center gap-2 text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                <ShieldCheck className="w-3.5 h-3.5 text-[#111827]" />
                Your HPCSA number is stored encrypted at rest and never shared.
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
